import {
    useForm,
    type SubmitHandler,
} from "react-hook-form"
import dayjs from "dayjs"

import useFormLocalStorage from "@/hooks/use-form-local-storage"
import { APP_VERSION } from "@/constants/general"
import Form from "@/components/forms/reusables/form"
import useFormStates from "@/hooks/use-form-states"
import type {
    YearlyFeePayment,
    YearlyFeePaymentFormInput,
} from "@/types"
import {
    useCreateYearlyFeePayment,
    useUpdateYearlyFeePayment,
} from "@/hooks/api/use-yearly-fee-payment"
import RHFNumberTextfield from "@/components/form-fields/rhf-number-textfield"
import { convertFromCents } from "@/helpers"
import RHFDatePicker from "../form-fields/rhf-date-picker"

const YearlyFeePaymentForm = ({
    yearlyFeePayment,
    feePayerId,
    closeCallback,
} : {
    yearlyFeePayment?: YearlyFeePayment,
    feePayerId?: number,
    closeCallback?: () => void,
}) => {

    const formDataId = `yearlyFeePaymentForm-${yearlyFeePayment?.id}-v${APP_VERSION}`

    const initialValues = {
        due_date: yearlyFeePayment ? (yearlyFeePayment.due_date ?? null) : dayjs().toISOString(),
        requested_amount_cents: yearlyFeePayment ? convertFromCents(yearlyFeePayment.requested_amount_cents) : '',
    }
    
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
    } = useForm({
        defaultValues: { ...initialValues },
    })

    const [isLoadedFromLocalStorage, handleFormReset] = useFormLocalStorage({
        control,
        formDataId,
        reset,
        initialValues
    })

    const {
        resetWithVersion,
        requestInProgress,
        setRequestInProgress,
        setError,
        error,
    } = useFormStates(handleFormReset)

    const mutationCreate = useCreateYearlyFeePayment(
        feePayerId,
        {
            setRequestInProgress,
            formDataId,
            onSuccessCallback: closeCallback,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            }
        },
    )

    const mutationUpdate = useUpdateYearlyFeePayment(
        yearlyFeePayment?.id,
        {
            setRequestInProgress,
            formDataId,
            onSuccessCallback: closeCallback,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            }
        },
    )

    const onSubmit: SubmitHandler<YearlyFeePaymentFormInput> = data => {
        setRequestInProgress(true)
        if (yearlyFeePayment?.id) {
            mutationUpdate.mutate(data)
        } else {
            mutationCreate.mutate(data)
        }
    }

    return (
        <Form
            onSubmit={handleSubmit(onSubmit)}
            heading={yearlyFeePayment ? 'Upravit platbu' : 'Vytvořit platbu'}
            closeCallback={closeCallback}
            isLoadedFromLocalStorage={isLoadedFromLocalStorage}
            isDirty={isDirty}
            resetWithVersion={resetWithVersion}
            requestInProgress={requestInProgress}
            buttonText={yearlyFeePayment ? 'Upravit' : 'Vytvořit'}
            error={error}
            setError={setError}
        >
            
            <RHFNumberTextfield
                name="requested_amount_cents"
                control={control}
                label="Výše platby"
                endAdornment="Kč"
                decimalScale={2}
                thousandSeparator
                disallowNegative
                required
            />
            <RHFDatePicker
                name="due_date"
                control={control}
                label="Datum splatnosti"
                showShortcuts
                required
            />
        </Form>
    )
}

export default YearlyFeePaymentForm
